import {
  Allergen,
  CatMedicalCondition,
  type CatActivityLevel,
  type CatBodyType,
} from "@/primary-models";

export type Breed =
  | "DOMESTIC_SHORT_HAIR"
  | "DOMESTIC_MEDIUM_HAIR"
  | "DOMESTIC_LONG_HAIR"
  | "ABYSSINIAN"
  | "AMERICAN_BOBTAIL"
  | "AMERICAN_CURL"
  | "AMERICAN_SHORTHAIR"
  | "BALINESE"
  | "BENGAL"
  | "BIRMAN"
  | "BRITISH_SHORTHAIR"
  | "BRITISH_LONGHAIR"
  | "BURMESE"
  | "BURMILLA"
  | "CHAUSIE"
  | "CHINCHILLA"
  | "CORNISH_REX"
  | "DEVON_REX"
  | "EXOTIC_SHORTHAIR"
  | "JAPANESE_BOBTAIL"
  | "KORAT"
  | "KURILIAN_BOBTAILS"
  | "LAPERM"
  | "MAINE_COON"
  | "MANX"
  | "MOGGIE"
  | "NORWEGIAN_FOREST_CAT"
  | "OCICAT"
  | "ORIENTAL_SHORTHAIR"
  | "PERSIAN"
  | "PETERBALD"
  | "PIXIE_BOB"
  | "RAGAMUFFIN"
  | "RAGDOLL"
  | "RUSSIAN_BLUE"
  | "SAVANNAH"
  | "SCOTTISH_FOLD"
  | "SIAMESE"
  | "SIBERIAN_FOREST_CAT"
  | "SINGAPURA"
  | "SNOWSHOE"
  | "SOMALI"
  | "SPHYNX"
  | "TIFFANIE"
  | "TONKINESE"
  | "TURKISH_ANGORA"
  | "TURKISH_VAN"
  | "OTHER"
  | "I_DONT_KNOW_THEIR_BREED"
  | "UNKNOWN";

export interface IEstimatedWeights {
  lower: number;
  estimated: number;
  upper: number;
}

export type AdultWeightProfile = Record<BodyType, IEstimatedWeights>;

/**
 * Identifies which adult weight profile a cat grows towards. Both values are optional because
 * several callers hold cats with unknown breed/sex; those fall back to an ungendered moggie.
 */
export interface ICatProfile {
  breed?: Breed | null;
  gender?: Gender | null;
}

export interface IBreed {
  value: Breed;
  text: string;
  maleWeights: AdultWeightProfile;
  femaleWeights: AdultWeightProfile;
  isMoggie: boolean;
  isPedigree: boolean;
  isLongHaired?: boolean;
}

export enum ECatResides {
  INDOOR = "INDOOR",
  OUTDOOR = "OUTDOOR",
}

export type AgeType = "kitten" | "adult" | "senior";
export type Gender = "BOY" | "GIRL" | "UNKNOWN";
export type BinaryGender = "BOY" | "GIRL";
export type LifeStyle = CatActivityLevel;
export type BodyType = CatBodyType;
export type PouchSize = "A" | "B" | "C" | "D" | "E" | "F";
export type Portion = 0.75 | 1 | 1.25 | 1.5 | 1.75 | 2;

export function isPouchSize(string: string): string is PouchSize {
  const pouchSizes: PouchSize[] = [ "A", "B", "C", "D", "E", "F" ];
  return pouchSizes.includes(string as PouchSize);
}

type TBodyTypeIcon = "catWeightChubby" | "catWeightJustRight" | "catWeightObese" | "catWeightSkinny";

export interface IBodyType {
  name: string;
  icon: TBodyTypeIcon;
}

export type BodyTypeCalorieMultiplier = Record<BodyType, number>;
export type LifeStyleCalorieMultiplier = Record<LifeStyle, number>;
export type MealPlanCalories = Record<PouchSize, number>;
export type RetailTrayPortions = Record<number, Portion>;

export interface IMealPlan {
  size: PouchSize;
  calories: number;
}

export interface IRetailTrayPortion {
  portion: Portion;
  calories: number;
}

export interface ICatAge {
  type?: AgeType;
  years?: number;
  months?: number;
  weeks?: number;
}

export interface INumeratorDenominator {
  numerator: number;
  denominator: number;
}
export interface IFraction {
  wholeNumber?: number;
  fraction?: INumeratorDenominator;
}

export type TDecimalRounding = "UP" | "DOWN";

export interface IMedicalCondition {
  value: CatMedicalCondition,
  title: string,
  aliases: string[],
}

export interface IAllergy {
  value: Allergen,
  title: string,
  aliases: string[],
}
